import type { ReactNode } from 'react';
import { Body1, Body1Strong, Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  description: {
    color: tokens.colorNeutralForeground3,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  body: {
    minHeight: 0,
    overflow: 'auto',
  },
  /* Opt-in fill (see `fill` prop): the shell grows to fill a bounded
     flex-column parent and its BODY becomes the scroll region (sticky header
     stays, footer pins). Default off so the ~25 content-height callers are
     unchanged. */
  rootFill: {
    flex: 1,
  },
  bodyFill: {
    flex: 1,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
  },
});

interface DataTableShellProps {
  title?: string;
  description?: string;
  controls?: ReactNode;
  children: ReactNode;
  footerStart?: ReactNode;
  footerEnd?: ReactNode;
  /**
   * Fill a bounded flex-column parent and make the BODY the scroll region
   * (sticky table header stays, footer pins). Off by default — only opt in
   * when the shell sits inside a height-constrained `overflow:hidden` flex
   * column (e.g. the Case queue). Leave off when the shell should size to its
   * content inside a scrolling page/tab.
   */
  fill?: boolean;
}

export function DataTableShell({
  children,
  controls,
  description,
  footerEnd,
  footerStart,
  title,
  fill = false,
}: DataTableShellProps) {
  const styles = useStyles();

  return (
    <div className={mergeClasses(styles.root, fill && styles.rootFill)}>
      {(title || controls) ? (
        <div className={styles.header}>
          {title ? (
            <div className={styles.titleBlock}>
              <Body1Strong>{title}</Body1Strong>
              {description ? <Caption1 className={styles.description}>{description}</Caption1> : null}
            </div>
          ) : null}
          {controls ? <div className={styles.controls}>{controls}</div> : null}
        </div>
      ) : null}
      <div className={mergeClasses(styles.body, fill && styles.bodyFill)}>{children}</div>
      {footerStart || footerEnd ? (
        <div className={styles.footer}>
          <Body1>{footerStart}</Body1>
          <Body1>{footerEnd}</Body1>
        </div>
      ) : null}
    </div>
  );
}
